import React from "react";
import {
  UseAssetsQuery,
  UseGetTour,
  UseSaveTour,
} from "../../../api/calls/Queries";
import { Popup, Scene, TourData } from "../../../Types";
import { useQueryClient } from "@tanstack/react-query";
import UploadPage from "./UploadPage";
import ListElement from "./ListElement";
import DalleWrapper from "./DalleWrapper";
import {
  useAssetListStore,
  useCurrentSceneStore,
  useEditingElementStore,
  useParametersStore,
} from "../../../api/Zustand";
import { RightArrow } from "../../Icons/left-side-bar-icons";
import { OPENAIENABLED } from "../../../App";
import AssetPreview from "./AssetPreview";
import { getLanguageFile } from "../../common/GetLanguageFile";

/* eslint import/no-webpack-loader-syntax: off */
//@ts-expect-error
import css from "!raw-loader!./assetlist.css";

const AssetList = () => {
  const queryClient = useQueryClient();
  const { currentSceneID } = useCurrentSceneStore();
  const { tourID } = useParametersStore();

  const assetList = useAssetListStore();
  const { setOpen, setClosed, setOpenForMap, setLoading } = useAssetListStore();

  const { id, type } = useEditingElementStore();

  const { mutateAsync: saveTour } = UseSaveTour();
  const { data: tourData, isLoading } = UseGetTour(tourID);
  const { data: assets, refetch } = UseAssetsQuery(tourID, assetList.type || 1);

  const [page, setPage] = React.useState<1 | 2 | 3>(1);
  const [selectedAssetID, setSelectedAssetID] = React.useState<
    number | string | undefined
  >();

  const currentScene = React.useMemo(() => {
    return tourData?.scenes.find((one) => one.id === currentSceneID);
  }, [tourData?.scenes, currentSceneID]);

  React.useEffect(() => {
    if (assetList.type === 1 && currentScene?.imageID) {
      setSelectedAssetID(currentScene?.imageID!);
    }
  }, [assetList.type, currentSceneID, tourData?.scenes, currentScene]);

  const setPano = React.useCallback(() => {
    if (assets?.find((one) => one.assetId === selectedAssetID)) {
      const newTourData = queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,

        scenes: tourData!.scenes.map((sc: Scene) =>
          sc.id === currentSceneID
            ? {
                ...sc,
                imageID: selectedAssetID,

                isNew: undefined,
                isUsingNewPano: undefined,
              }
            : sc
        ),
      });
      saveTour(newTourData as TourData, {
        onSuccess: () => {
          setSelectedAssetID(undefined);
          setClosed();
          // setAssetList({ isOpen: false, type: undefined });
        },
      });
    }
  }, [
    setClosed,
    assets,
    currentSceneID,
    queryClient,
    saveTour,
    selectedAssetID,
    tourData,
    tourID,
  ]);

  const setImage = React.useCallback(() => {
    if (assets?.find((one) => one.assetId === selectedAssetID)) {
      const actualPopState = tourData?.popups.find((one) => one.id === id);
      const selectedAsset = assets.find(
        (one) => one.assetId === selectedAssetID
      );

      const newTourData = queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        popups: tourData?.popups.map((pop: Popup) =>
          pop.id === id
            ? {
                ...actualPopState,
                imageID: selectedAsset?.assetId,
                image: selectedAsset?.assetKeyFull,
                imageName: selectedAsset?.assetDescription,
              }
            : pop
        ),
      });
      saveTour(newTourData as TourData, {
        onSuccess: () => {
          setSelectedAssetID(undefined);
          setClosed();
          // setAssetList({ isOpen: false, type: undefined });
        },
      });
    }
  }, [
    id,
    assets,
    queryClient,
    saveTour,
    tourData,
    tourID,
    selectedAssetID,
    setClosed,
  ]);

  const setAudio = React.useCallback(async () => {
    if (type === "scene") {
      const newTourData = await queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: [
          ...tourData!.scenes.map((sc: Scene) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  audio: selectedAssetID,
                }
              : sc
          ),
        ],
      });

      saveTour(newTourData as TourData, {
        onSuccess: () => {
          setSelectedAssetID(undefined);
          setClosed();
          // setAssetList({ isOpen: false, type: undefined });
        },
      });
    } else {
      const newTourData = await queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: [
          ...tourData!.scenes.map((sc: Scene) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  hotspots: sc.hotspots.map((hs) =>
                    hs.id === id ? { ...hs, target: selectedAssetID } : hs
                  ),
                }
              : sc
          ),
        ],
      });

      saveTour(newTourData as TourData, {
        onSuccess: () => {
          setSelectedAssetID(undefined);
          setClosed();
          // setAssetList({ isOpen: false, type: undefined });
        },
      });
    }
  }, [
    selectedAssetID,
    currentSceneID,
    id,
    type,
    tourID,
    queryClient,
    saveTour,
    setClosed,
    tourData,
  ]);

  const setMap = React.useCallback(() => {
    if (assets?.find((one) => one.assetId === selectedAssetID)) {
      const newTourData = queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        maps: tourData?.maps?.map((map) =>
          map.id === assetList.mapID
            ? { ...map, assetId: selectedAssetID }
            : map
        ),
      });
      saveTour(newTourData as TourData, {
        onSuccess: () => {
          setSelectedAssetID(undefined);
          setClosed();
          // setAssetList({ isOpen: false, type: undefined });
        },
      });
    }
  }, [
    assets,
    queryClient,
    saveTour,
    tourData,
    tourID,
    selectedAssetID,
    assetList.mapID,
    setClosed,
  ]);

  const useButtonIsDisabled: boolean | undefined = React.useMemo(() => {
    if (isLoading) return true;
    else if (page === 3 && selectedAssetID) {
      return false;
    } else if (!assets?.find((one) => one.assetId === selectedAssetID))
      return true;
  }, [assets, isLoading, page, selectedAssetID]);

  if (assetList.isOpen && tourData && !isLoading)
    return (
      <>
        <style>{css}</style>
        <div
          onClick={setClosed}
          style={{
            background: assetList.forMap ? 0 : "#0008",
          }}
          className="asset-list"
        >
          <div className="main-panel" onClick={(e) => e.stopPropagation()}>
            <header>
              {assetList.forMap ? (
                <button id="assetListClose" onClick={setClosed}>
                  <RightArrow
                    height={"1rem"}
                    width={"1rem"}
                    style={{ transform: "rotate(180deg)" }}
                  />
                  {getLanguageFile("overlapping_panel.back_to_maps")}{" "}
                </button>
              ) : (
                <>
                  <h2>
                    {assetList.type === 1
                      ? getLanguageFile("overlapping_panel.panos")
                      : assetList.type === 2
                      ? getLanguageFile("overlapping_panel.images")
                      : getLanguageFile("overlapping_panel.audio")}{" "}
                  </h2>
                  <button
                    disabled={assetList.isLoading}
                    id="assetListClose"
                    onClick={
                      setClosed
                      // setAssetList({ isOpen: false, type: undefined })
                    }
                  >
                    {getLanguageFile("overlapping_panel.close")}
                  </button>
                </>
              )}
            </header>
            <div className="list-background">
              <div className="selector-wrapper">
                <button
                  id="selector"
                  className={page === 1 ? "active" : undefined}
                  onClick={() => setPage(1)}
                >
                  {assetList.type === 1
                    ? getLanguageFile("overlapping_panel.panos_library")
                    : assetList.type === 2
                    ? getLanguageFile("overlapping_panel.images_library")
                    : getLanguageFile("overlapping_panel.audio_library")}
                </button>
                <button
                  id="selector"
                  className={page === 2 ? "active" : undefined}
                  onClick={() => {
                    setPage(2);
                    setSelectedAssetID(undefined);
                  }}
                >
                  {getLanguageFile("overlapping_panel.upload")}
                </button>

                {OPENAIENABLED && assetList.type === 2 && (
                  <button
                    id="selector"
                    className={page === 3 ? "active" : undefined}
                    onClick={() => {
                      setPage(3);
                      setSelectedAssetID(undefined);
                    }}
                  >
                    Genera con AI
                  </button>
                )}

                <div className="space-filler"></div>
              </div>

              {assets && assetList.type && (
                <div className="content-wrapper">
                  <div className="content">
                    {page === 1 ? (
                      <ListElement
                        assets={assets}
                        assetType={assetList.type}
                        selectedAsset={selectedAssetID}
                        setSelectedAsset={setSelectedAssetID}
                      />
                    ) : page === 2 ? (
                      <UploadPage
                        refetch={refetch}
                        assetType={assetList.type!}
                        setSelectedAsset={setSelectedAssetID}
                        setPage={setPage}
                        assets={assets}
                      />
                    ) : page === 3 ? (
                      <DalleWrapper
                        setPage={setPage}
                        refetch={refetch}
                        tourData={tourData}
                        selectedAsset={selectedAssetID}
                        setSelectedAsset={setSelectedAssetID}
                      ></DalleWrapper>
                    ) : null}
                  </div>
                  {page === 1 && !assetList.forMap && selectedAssetID && (
                    <div style={{ width: 300, display: "flex" }}>
                      <AssetPreview
                        tourData={tourData}
                        selectedAsset={selectedAssetID as number}
                        assets={assets}
                        assetListOpenType={assetList.type}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "10px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {assetList.type === 1 && currentScene?.imageID && (
                <button id="file-upload" onClick={setClosed}>
                  {getLanguageFile("generics.cancel")}
                </button>
              )}
              {assetList.type === 2 &&
                tourData.popups.find((one) => one.id === id)?.image && (
                  <button id="file-upload" onClick={setClosed}>
                    {getLanguageFile("generics.cancel")}
                  </button>
                )}

              <button
                onClick={() => {
                  if (assetList.forMap) {
                    setMap();
                    return;
                  }
                  if (assetList.type === 1) setPano();
                  else if (assetList.type === 2) setImage();
                  else setAudio();
                }}
                id="file-upload"
                disabled={useButtonIsDisabled}
              >
                {assetList.type === 1
                  ? getLanguageFile("overlapping_panel.use_pano")
                  : assetList.type === 2
                  ? getLanguageFile("overlapping_panel.use_image")
                  : getLanguageFile("overlapping_panel.use_audio")}
              </button>
            </div>
          </div>
        </div>
      </>
    );
};

export default AssetList;
